// stores/useSystemConfigurationsStore.ts
import { create } from "zustand";
import {
    GenericSystemConfigurationModel,
    UserSettingModel,
    CompanySettingModel,
    UserTransactionsSettingResponseModel,
    DailyTransactionTypeEnum, ApiGenericSystemConfigurationModel, ResponseBaseModel, SystemModulesConfigurationModel
} from "../../../../models";
import {
    getCompanySettings
} from "../businessLogic/transactionBl";
import {
    getUserSetting,
    getUserTransactionsSettings
} from "../../../../serviceBroker/userApiServiceBroker";
import {
    getUserId,
    secureLocalStorageGet,
    secureLocalStorageSet
} from "../../../../utils";
import { SystemConfiguration } from "../../../../configuration";
import {getAllSystemLightModule} from "../../../../serviceBroker/systemConfigurationApiServiceBroker";
import {getClientGenericConfiguration} from "../../../../serviceBroker/configurationsApiServiceBroker";
import {userTransactionsSettingInitialValues} from "../initialValues/initialValues";

let syncIntervalId: NodeJS.Timeout | null = null;
let isAutoSyncInitialized = false;
interface SystemConfigurationsState {
    // Configuration Data
    userSetting: UserSettingModel | null;
    companySetting: CompanySettingModel | null;
    userTransactionSetting: UserTransactionsSettingResponseModel | null;
    userTransactionSettingList: UserTransactionsSettingResponseModel[];
    systemModulesConfigurations:SystemModulesConfigurationModel[]|null,
    genericSystemConfigurations: GenericSystemConfigurationModel | null;
    // State flags
    isLoaded: boolean;
    loading: boolean;
    // Actions
    loadConfigurations: (transactionType: DailyTransactionTypeEnum, formId: number) => Promise<void>;
    setUserSetting: (setting: UserSettingModel) => void;
    setCompanySetting: (setting: CompanySettingModel) => void;
    setUserTransactionSetting: (setting: UserTransactionsSettingResponseModel) => void;
    setUserTransactionSettingList: (list: UserTransactionsSettingResponseModel[]) => void;
    setSystemModulesConfigurations: (list:SystemModulesConfigurationModel[]) => void;
    setGenericSystemConfigurations: (config: GenericSystemConfigurationModel) => void;
    setIsLoaded: (isLoaded: boolean) => void;
    setLoading: (loading: boolean) => void;
    resetState: () => void;
}

export const useSystemConfigurationsStore = create<SystemConfigurationsState>((set,get) => ({
    // Initial state
   userSetting: null,
    companySetting: null,
    userTransactionSetting: null,
    userTransactionSettingList: [],
    systemModulesConfigurations:[],
    genericSystemConfigurations: null,
    isLoaded: false,
    loading: false,

    // Action to load all configurations
    loadConfigurations: async (transactionType: DailyTransactionTypeEnum, formId: number) => {
        set({loading: true});

        try {
            // Run async calls in parallel
            const [userSetting, companySetting, userTransactionSettingList, genericSystemConfigurations, systemModulesConfiguration] = await Promise.all([
                getUserSetting(getUserId()),
                getCompanySettings(),
                getUserTransactionsSettings([
                        DailyTransactionTypeEnum.SalesInvoice,
                        DailyTransactionTypeEnum.PurchasesInvoice,
                        DailyTransactionTypeEnum.SalesRefund,
                        DailyTransactionTypeEnum.PurchasesRefund,
                        DailyTransactionTypeEnum.Order,
                        DailyTransactionTypeEnum.ShowPrice,
                        DailyTransactionTypeEnum.POSSales,
                        DailyTransactionTypeEnum.POSSalesRefund,
                        DailyTransactionTypeEnum.POSRestaurant,
                        DailyTransactionTypeEnum.Reserve_Restaurant_Bill,
                        DailyTransactionTypeEnum.ReturnPOSRestaurant,],
                    true,
                    `${SystemConfiguration.cacheKey_Configurations.setting.userTransactionSettingsList}_${formId}` // Add formId to cache key
                ),
                getGenericSystemConfigurations(),
                getAllSystemLightModule(),
            ]);
            // Update Zustand store once
            set({
                userSetting,
                companySetting,
                userTransactionSettingList,
                userTransactionSetting: getUserTransactionSetting(transactionType, userTransactionSettingList),
                genericSystemConfigurations: genericSystemConfigurations,
                systemModulesConfigurations: systemModulesConfiguration?.Result || [],
                isLoaded: true,
                loading: false,
            });
        } catch (error) {
            console.error("Failed to load system configurations:", error);
            set({
                loading: false,
            });
        }
    },

    // Setters
    setGenericSystemConfigurations: (config) => set({ genericSystemConfigurations: config }),
    setUserSetting: (setting) => set({ userSetting: setting }),
    setCompanySetting: (setting) => set({ companySetting: setting }),
    setUserTransactionSetting: (setting) => set({ userTransactionSetting: setting }),
    setUserTransactionSettingList: (list) => set({ userTransactionSettingList: list }),
    setSystemModulesConfigurations: (list) => set({ systemModulesConfigurations: list }),
    setIsLoaded: (isLoaded) => set({ isLoaded: isLoaded }),
    setLoading: (loading) => set({ loading }),
    resetState: () =>
        set({
            genericSystemConfigurations: null,
            userSetting: null,
            companySetting: null,
            userTransactionSettingList: [],
            userTransactionSetting: null,
            systemModulesConfigurations: null,
            isLoaded: false,
            loading: false,
        }),
}));
//#region helper functions
const getUserTransactionSetting = (
    transactionType: DailyTransactionTypeEnum,
    userTransactionSettingList: UserTransactionsSettingResponseModel[],
): UserTransactionsSettingResponseModel => {
    return transactionType == DailyTransactionTypeEnum.None
        ? userTransactionsSettingInitialValues
        : userTransactionSettingList.find((x) => x.TransactionType_ID === transactionType) ||
        userTransactionsSettingInitialValues;
};
const getGenericSystemConfigurations = async (): Promise<GenericSystemConfigurationModel> => {
    let genericSystemConfigurations: GenericSystemConfigurationModel;
    // Load cached config if available
    const cachedConfig = secureLocalStorageGet(
        SystemConfiguration.cacheKey_Configurations.setting.genericSystemConfiguration
    );
    if (cachedConfig) {
        genericSystemConfigurations = JSON.parse(cachedConfig as string);
    } else {
        const response = await getClientGenericConfiguration();
        genericSystemConfigurations = mapApiResponseToConfig(response);
        secureLocalStorageSet(
            SystemConfiguration.cacheKey_Configurations.setting.genericSystemConfiguration,
            JSON.stringify(genericSystemConfigurations)
        );
    }

    return genericSystemConfigurations;
};
function mapApiResponseToConfig(
    apiResponse: ResponseBaseModel<ApiGenericSystemConfigurationModel[]>
): GenericSystemConfigurationModel {
    const defaultValues: GenericSystemConfigurationModel = {
        RestruantTableRecolerd: false,
        Is_Support_Handel_Mobile_CallApi: false,
        Enable_Add_Item_Image_In_Save_Transaction: false,
        Is_Send_Order_For_Preparing_Screen: false,
        Is_Search_For_Transaction_By_Created_User: false,
        IsPlaySoundOnBarcodeFound: false,
        Is_Enable_Refresh_Timer: false,
        IsEnableToShangeInstallmentPlanAmount: false,
        IsIncludeItemImageInMenuGenratedPdf: false,
        Is_Item_Serial_Requierd: false,
        Is_Item_Serial_Unique: false,
        Is_Save_Transaction_Direct_Connect_To_DB: false,
        Is_Log_Api_Requests: false,
        Is_Distrbute_PriceSale_For_ItemUnit: false,
        Is_Reset_Serial_Of_Order_After_Any_Close_Day: false,
        IsReturnOtherItemUnitsInfoWhenSearchByCachedBarcode: false,
        IsShowItemImageInItemGridTransaction: false,
        IsPrintItemPrescriptionAfterSale: false,
        IsShowCustomerDetailAddressInPrint: false,
        IsFormatNumberToIntIfItIsNotDecimal: false,
        IsPutPnlCustomerInsidePanlButtonPOS: false,
        IsBindGridWithCustomCustomerResult: false,
        IsShowDailogOfButtonNumberOnPOSGrid: false,
        IsEnablePartialPaymentForResturantTable: false,
        IsDirectUploadSalesToeInvoice: false,
        IsEnableToSetSenderEmailAndPassword: false,
        IsSendDetailReportTransactiononEmail: false,
        Is_Get_Category_Code_Depend_On_Parent: false,
        IsSavePurchaseTransactionFromSalesForBalanceLessTHanZero: false,
        IsEnableToSetPriceCoseInTransaction: false,
        IsEnableToUploadAndAttachDocumentInTransaction: false,
        Timer_For_Upload_Close_Day_InMinutes: 60,
        Default_Payment_Type_For_Multiple_Payment: 1,
        Timer_For_Preparing_Order_Screen_InSeconds: 30,
        IntervalTimeInMinutesEinvoceSyenc: 10,
        SecondsWaitingToTakeSecondCallOfSameNumber: 60,
        ButtonControlCountInPosScreen: 3,
        FontSizeOfPanelInfoWithCustomer: 0,
        ControlsHieghtOfPnlInfoWithCustomer: 0,
        BackColorReturantTrasnferdTable: 0,
        NumberOfControlsOnShowFormWillDisplyAutoScroll: 50,
        PercentageWidthOfTreeView: 0,
        SecondsWaitingToPushAnotherTransactionToEinvoice: 10,
        DefultTobaccoTaxPercentage: 100,
        MinimumTobaccoTaxValue: 25,
        Due_Date_Installment_Alert: 100,
        MaxFileToUploadInKB: 500,
        Folder_Path_To_Push_Preparing_Order: "",
        AppleAplicationUrl: "",
        AndroidAplicationUrl: "",
        FilePlaySoundOnBarcodeFound: "",
        EgyptEtaTokenUrl: "",
        ApiServiceDownloadItemMenuOnScanQr: "",
        Service_Local_Uri: "",
        SmallestSizeScreentToShowMaximuzedForm: "",
        ButtonSizePos: "",
        ButtonKeyBoardSizePos: "",
        ButtonSizePosResturant: "",
        ButtonKeyBoardSizePosResturant: "",
        BackColorCategory: "",
        FilesTypeAllowedToUpload: "image/png,image/jpg,image/jpeg,application/pdf",
        Installment_Plan: {
            DaysOfInstallmentDistrubite: 30,
            ModValue: 100,
            TransferFractionMoneyToFirstInstallment: true
        },
        Printer_Settings: {
            NumberCopyPrintLocalOrder: 1,
            NumberCopyPrintDeliveryOrder: 1,
            NumberCopyPrintOutOrder: 1
        },
        Set_Serial_Pos_Depend_On_Employee: null,
        TrnsactionTypeIdsToShowConsignItemOnly: null,
        TrnsactionTypeIdsToExecludeConsignItem: null,
        CategoryIdsRemoveFromPreparingPrintingReport: null,
        TransactionTypeIdsConfigureToSavePurchaseFromTransaction: null,
        TrnsactionTypeIdsToIncludeGenrateQR: null
    };

    if (!apiResponse || !apiResponse.Result) {
        return defaultValues;
    }

    const configItems = apiResponse.Result;
    const config: GenericSystemConfigurationModel = { ...defaultValues };

    configItems.forEach(item => {
        const propertyName = item.Code as keyof GenericSystemConfigurationModel;
        if (!(propertyName in config)) return;
        const expectedType = typeof config[propertyName];
        const defaultForThisProperty = defaultValues[propertyName];

        if (expectedType === 'object' && defaultForThisProperty !== null) {
            try {
                if (typeof item.Config_Value === 'string') {
                    (config as any)[propertyName] = JSON.parse(item.Config_Value);
                } else {
                    (config as any)[propertyName] = defaultForThisProperty;
                }
            } catch {
                (config as any)[propertyName] = defaultForThisProperty;
            }
        } else {
            (config as any)[propertyName] = convertValue(
                item.Config_Value,
                expectedType,
                defaultForThisProperty,
                propertyName as string
            );
        }
    });

    return config;
}
function convertValue(value: any, expectedType: string, defaultValue: any, propertyName?: string): any {
    if (typeof value === expectedType) return value;
    if (typeof value === 'string') {
        if (expectedType === 'boolean') {
            return value.toLowerCase() === 'true';
        }
        if (expectedType === 'number') {
            const num = Number(value);
            return isNaN(num) ? defaultValue : num;
        }
        if (expectedType === 'object') {
            try {
                return JSON.parse(value);
            } catch {
                if (
                    propertyName &&
                    [
                        'TrnsactionTypeIdsToIncludeGenrateQR',
                        'TrnsactionTypeIdsToShowConsignItemOnly',
                        'TrnsactionTypeIdsToExecludeConsignItem',
                        'CategoryIdsRemoveFromPreparingPrintingReport',
                        'TransactionTypeIdsConfigureToSavePurchaseFromTransaction'
                    ].includes(propertyName)
                ) {
                    const items = value.split(',').map((item: string) => Number(item.trim()));
                    return items.every(isNaN) ? [] : items;
                }
                return defaultValue;
            }
        }
    }
    return defaultValue;
}
//#endregion
